@if (inventoryItem$ | async; as item) {
  <div class="inventory-item-overview-page">
    <app-header
      [title]="'TITLES.GOODS' | translate"
      [subtitle]="'COMPONENTS.INVENTORY_ITEM_OVERVIEW.SUBTITLE' | translate"
    >
      <div header-actions class="hidden lg:block">
        <app-navbar-profile-info></app-navbar-profile-info>
      </div>
    </app-header>

    <div class="inventory-item-overview-page__content py-6">
      @if (isLoading) {
        <div class="inventory-item-overview-page__overlay">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">{{ 'MESSAGES.LOADING' | translate }}</span>
          </div>
        </div>
      }

      <div class="inventory-item-overview__topbar">
        <app-back-button [fallbackUrl]="inventoryItemsBackUrl" />
        <div class="inventory-item-overview__topbar-actions">
          <button
            type="button"
            class="inventory-item-overview__topbar-button inventory-item-overview__topbar-button--secondary"
            [disabled]="isHistoryLoading"
            (click)="openHistoryPanel()"
          >
            {{ 'COMPONENTS.INVENTORY_ITEM_OVERVIEW.HISTORY.BUTTON' | translate }}
          </button>

          @if (!item.isArchived) {
            <button
              type="button"
              class="inventory-item-overview__topbar-button inventory-item-overview__topbar-button--secondary"
              (click)="archiveInventoryItem()"
            >
              {{ 'BUTTONS.ARCHIVE' | translate }}
            </button>
          } @else {
            <button
              type="button"
              class="inventory-item-overview__topbar-button inventory-item-overview__topbar-button--secondary"
              (click)="unarchiveInventoryItem()"
            >
              {{ 'BUTTONS.UNARCHIVE' | translate }}
            </button>
          }
        </div>
      </div>

      @if (item.isArchived) {
        <p class="inventory-item-overview__archived-note">
          {{ 'MESSAGES.ARCHIVED_INVENTORY_ITEM' | translate }}
        </p>
      }

      <app-card-shell class="inventory-item-overview__summary-card" padding="md">
        <div class="inventory-item-overview__hero">
          <div class="inventory-item-overview__gallery">
            <div class="inventory-item-overview__main-image">
              @if (activeImageUrl !== notFound) {
                <div class="inventory-item-overview__main-image-stage">
                  <img
                    class="inventory-item-overview__main-image-tag"
                    [src]="activeImageUrl"
                    [alt]="'COMPONENTS.INVENTORY_ITEM_OVERVIEW.IMAGE_ALT' | translate"
                  />
                </div>
              } @else {
                <div
                  class="inventory-item-overview__empty-image"
                  appNoImage
                  height="192px"
                  width="192px"
                  borderRadius="24px"
                  fontSize="40px"
                >
                  {{ itemTitle | abbreviation }}
                </div>
              }
            </div>

            <div class="inventory-item-overview__thumbs">
              @for (slot of thumbnailSlots; track slot.id) {
                <button
                  type="button"
                  class="inventory-item-overview__thumb w-14 h-14 rounded-lg flex items-center justify-center overflow-hidden border-2"
                  [class.border-blue-500]="slot.id === activeImageSlotId"
                  [class.border-transparent]="slot.id !== activeImageSlotId"
                  (click)="onOpenImageViewer(slot.id)"
                >
                  <img
                    class="inventory-item-overview__thumb-image"
                    [src]="slot.imageUrl"
                    [alt]="'COMPONENTS.INVENTORY_ITEM_OVERVIEW.IMAGE_ALT' | translate"
                  />
                </button>
              }

              @if (!item.isArchived && canAddMoreImages && nextAvailableImageSlotId) {
                <app-file-upload
                  id="inventory-item-add-image"
                  class="inventory-item-overview__thumb-upload"
                  [fileExtensions]="imageExtensions"
                  [path]="nextImageUploadPath"
                  [fileTitle]="nextAvailableImageSlotId"
                  (uploadedFileEvent)="onAdditionalImageUpload($event)"
                  (deletedFileEvent)="onAdditionalImageDelete()"
                >
                  <ng-template #customTemplate let-fileInputId="fileInputId">
                    <label
                      [attr.for]="fileInputId"
                      class="inventory-item-overview__thumb inventory-item-overview__thumb--add w-14 h-14 rounded-lg flex items-center justify-center border-2 border-transparent cursor-pointer"
                    >
                      <svg class="w-5 h-5 text-neutral-400" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M12 5V19M5 12H19"
                          stroke="currentColor"
                          stroke-width="1.8"
                          stroke-linecap="round"
                        />
                      </svg>
                    </label>
                  </ng-template>
                </app-file-upload>
              }
            </div>
          </div>

          <div class="inventory-item-overview__summary">
            <div class="inventory-item-overview__key-info">
              @for (field of heroKeyInfoFields; track field.id) {
                <div class="inventory-item-overview__key-info-item">
                  <p class="inventory-item-overview__key-info-label">
                    {{ field.labelKey | translate }}
                  </p>

                  @if (field.valueKey) {
                    <span [ngClass]="field.badgeClass || field.valueClass">
                      {{ field.valueKey | translate }}
                    </span>
                  } @else {
                    <p class="inventory-item-overview__value" [ngClass]="field.valueClass">
                      {{ field.value }}
                    </p>
                  }

                  @if (field.secondaryValue) {
                    <p class="inventory-item-overview__secondary-value">
                      {{ field.secondaryValue }}
                    </p>
                  }
                </div>
              }
            </div>

            <div class="inventory-item-overview__metrics">
              @for (metric of heroMetricCards; track metric.id) {
                <div class="inventory-item-overview__metric-card" [ngClass]="metric.cardClass">
                  <p class="inventory-item-overview__metric-label">
                    {{ metric.labelKey | translate }}
                  </p>
                  <p class="inventory-item-overview__metric-value" [ngClass]="metric.valueClass">
                    @switch (metric.valueFormat) {
                      @case ('percent') {
                        {{ metric.value | localizedPercent:(metric.emptyValue || '0%') }}
                      }
                      @case ('date') {
                        {{ metric.value | localizedDate:(metric.emptyValue || notFound) }}
                      }
                      @default {
                        {{ metric.value | localizedNumber:(metric.emptyValue || '0') }}
                      }
                    }
                  </p>
                  @if (metric.supportTextKey) {
                    <p class="inventory-item-overview__metric-support inventory-item-overview__metric-support--link">
                      {{ metric.supportTextKey | translate }}
                    </p>
                  } @else if (metric.supportText) {
                    <p class="inventory-item-overview__metric-support">
                      {{ metric.supportText }}
                    </p>
                  }
                </div>
              }
            </div>
          </div>
        </div>
      </app-card-shell>

      <app-card-shell class="inventory-item-overview__tabs-card mt-6" padding="md">
        <div class="inventory-item-overview__tabs">
          <app-tabs
            class="inventory-item-overview__tabs-shell"
            [customClass]="'inventory-item-overview-tabs'"
            [items]="overviewTabs"
            [activeId]="activeOverviewTab"
            (activeIdChange)="onOverviewTabChange($event)"
          ></app-tabs>

          <div class="inventory-item-overview__tab-panel">
            @switch (activeOverviewTab) {
              @case ('info') {
                <app-inventory-item-info-tab
                  [companyId]="companyId"
                  [itemId]="itemId"
                  [inventoryItem]="item"
                  [reloadToken]="infoTabReloadToken"
                  (inventoryItemChange)="onInventoryItemChange($event)"
                  (concurrencyConflict)="onConcurrencyConflict()"
                />
              }

              @case ('components') {
                <app-inventory-item-components-tab
                  [companyId]="companyId"
                  [itemId]="itemId"
                  [inventoryItemOptions]="inventoryItemOptions"
                  [isReadonly]="item.isArchived === true"
                />
              }

              @case ('finances') {
                <app-inventory-item-finances-tab
                  [companyId]="companyId"
                  [itemId]="itemId"
                  [inventoryItem]="item"
                  (inventoryItemChange)="onInventoryItemChange($event)"
                  (concurrencyConflict)="onConcurrencyConflict()"
                />
              }

              @case ('warehouse') {
                <app-inventory-item-warehouse-tab
                  [companyId]="companyId"
                  [itemId]="itemId"
                  [inventoryItem]="item"
                  [unitsOfMeasureOptions]="unitsOfMeasureOptions"
                  (inventoryItemChange)="onInventoryItemChange($event)"
                  (concurrencyConflict)="onConcurrencyConflict()"
                />
              }

              @case ('identification') {
                <app-inventory-item-identification-tab
                  [companyId]="companyId"
                  [itemId]="itemId"
                  [inventoryItem]="item"
                  (inventoryItemChange)="onInventoryItemChange($event)"
                  (concurrencyConflict)="onConcurrencyConflict()"
                />
              }

              @case ('files') {
                <app-inventory-item-files-tab
                  [companyId]="companyId"
                  [itemId]="itemId"
                  [isReadonly]="item.isArchived === true"
                  [galleryRefreshToken]="filesTabReloadToken"
                  (imageFilesChanged)="onImageFilesChanged()"
                />
              }

              @case ('description') {
                <app-inventory-item-description-tab
                  [companyId]="companyId"
                  [itemId]="itemId"
                  [inventoryItem]="item"
                  (inventoryItemChange)="onInventoryItemChange($event)"
                  (concurrencyConflict)="onConcurrencyConflict()"
                />
              }

              @case ('unitPricing') {
                <app-inventory-item-uom-and-prices-tab
                  [companyId]="companyId"
                  [itemId]="itemId"
                  [inventoryItem]="item"
                  [unitsOfMeasureOptions]="unitsOfMeasureOptions"
                  (inventoryItemChange)="onInventoryItemChange($event)"
                  (concurrencyConflict)="onConcurrencyConflict()"
                />
              }
            }
          </div>
        </div>
      </app-card-shell>

      <app-inventory-item-image-viewer
        [isOpen]="isImageViewerOpen"
        [title]="itemTitle"
        [images]="thumbnailSlots"
        [activeImageId]="activeImageSlotId"
        (closed)="onCloseImageViewer()"
      />

      <app-inventory-item-history-panel
        [isOpen]="isHistoryPanelOpen"
        [itemTitle]="itemTitle"
        [changes]="inventoryItemChanges"
        (closed)="closeHistoryPanel()"
      />

      <ng-template #concurrencyWarningTpl>
        {{ 'ERRORS.CONCURRENCY_CONFLICT' | translate }}
      </ng-template>

      <app-toasts aria-live="polite" aria-atomic="true"></app-toasts>
    </div>
  </div>
}
